import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout as logoutService } from '../services/auth';
import { clearCredentials, selectCurrentUser } from '../store/slices/authSlice';
import { useAdminNotifications } from '../context/AdminNotificationsContext';
import Logo from './Logo';
import './Header.css';

const formatOrderDate = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
};

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const { pendingCount, pendingOrders } = useAdminNotifications();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotificationPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      // Call backend to invalidate token
      await logoutService();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear Redux state
      dispatch(clearCredentials());
      // Redirect to login
      navigate('/admin/login');
    }
  };

  const getUserInitials = () => {
    if (user?.name) {
      return user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    return 'AD';
  };

  return (
    <header className="admin-header">
      <div className="header-left">
        <Link to="/admin/dashboard" className="logo-link">
          <Logo />
          <span className="logo-text">FM ULTRATECH FARMS</span>
        </Link>
      </div>
      
      <div className="header-center">
        <div className="search-bar">
          <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input type="text" placeholder="Search..." />
        </div>
      </div>
      
      <div className="header-right">
        <div className="header-notification-wrap" ref={notificationRef}>
          <button
            type="button"
            className="header-icon-btn header-notification-btn"
            onClick={() => setShowNotificationPanel((prev) => !prev)}
            aria-label={pendingCount > 0 ? `${pendingCount} pending orders` : 'Notifications'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            {pendingCount > 0 && (
              <span className="header-notification-badge">
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </button>
          {showNotificationPanel && (
            <div className="header-notification-panel">
              <div className="header-notification-header">
                <span className="header-notification-title">Notifications</span>
                {pendingCount > 0 && (
                  <span className="header-notification-count">{pendingCount} pending order{pendingCount !== 1 ? 's' : ''}</span>
                )}
              </div>
              <div className="header-notification-list">
                {pendingOrders.length === 0 ? (
                  <div className="header-notification-empty">No pending orders</div>
                ) : (
                  pendingOrders.map((order) => (
                    <Link
                      key={order.id}
                      to="/admin/orders"
                      className="header-notification-item"
                      onClick={() => setShowNotificationPanel(false)}
                    >
                      <span className="header-notification-item-order">{order.order_number || `#${order.id}`}</span>
                      <span className="header-notification-item-customer">{order.user?.email || order.user?.name || 'Customer'}</span>
                      <span className="header-notification-item-date">{formatOrderDate(order.created_at)}</span>
                    </Link>
                  ))
                )}
              </div>
              {pendingCount > 0 && (
                <Link
                  to="/admin/orders"
                  className="header-notification-viewall"
                  onClick={() => setShowNotificationPanel(false)}
                >
                  View all orders
                </Link>
              )}
            </div>
          )}
        </div>
        <button className="header-icon-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>
        <div 
          className="admin-profile" 
          onClick={() => setShowDropdown(!showDropdown)}
          ref={dropdownRef}
        >
          <div className="profile-avatar">
            {user?.name ? (
              <span className="avatar-initials">{getUserInitials()}</span>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            )}
          </div>
          <span className="admin-text">{user?.name || 'Admin'}</span>
          <svg 
            className={`dropdown-arrow ${showDropdown ? 'open' : ''}`} 
            width="12" 
            height="12" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
          
          {showDropdown && (
            <div className="profile-dropdown">
              <div className="dropdown-header">
                <div className="dropdown-avatar">
                  {user?.name ? (
                    <span className="avatar-initials">{getUserInitials()}</span>
                  ) : (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  )}
                </div>
                <div className="dropdown-user-info">
                  <div className="dropdown-name">{user?.name || 'Admin User'}</div>
                  <div className="dropdown-email">{user?.email || ''}</div>
                </div>
              </div>
              <div className="dropdown-divider"></div>
              <button className="dropdown-item logout-item" onClick={handleLogout}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
